import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Brush, Trash2, Upload, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { adminApi } from "@/lib/adminApi";
import { AuthImage, useAuthImage } from "@/components/admin/todos/AuthImage";
import { ImageMarkup } from "@/components/admin/todos/ImageMarkup";
import { StatusSelect, err, fmtDate } from "@/components/admin/todos/TodosTab";

const fmtTime = (d) => new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const HIST = { created: () => "Created", status: (h) => `${h.from} → ${h.to}`, move: (h) => `Moved from ${h.from} to ${h.to}`, attachment: (h) => h.to };

const LinkPicker = ({ client, project, onPick, onClear }) => {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState(null);
  useEffect(() => {
    if (opts === null && !q) return undefined;
    const t = setTimeout(() => adminApi.get("/todos/link-options", { params: { q } }).then((r) => setOpts(r.data)), 200);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  if (client || project) {
    return (
      <div className="td-chips" data-testid="todo-link-chips">
        {project && <Link className="td-chip" to={`/admin/studio/projects/${project.id}`} data-testid="todo-link-project">Project · {project.name}</Link>}
        {client && <Link className="td-chip" to={`/admin/studio/clients/${client.id}`} data-testid="todo-link-client">Client · {client.full_name}</Link>}
        <button className="td-icon" onClick={onClear} aria-label="Remove link" data-testid="todo-link-clear"><X size={14} /></button>
      </div>
    );
  }
  return (
    <div className="td-picker">
      <input className="hx-input" placeholder="Search Studio clients or projects (optional)" value={q} onFocus={() => opts === null && setQ(" ")} onChange={(e) => setQ(e.target.value)} data-testid="todo-link-search" />
      {opts && (opts.clients.length + opts.projects.length > 0) && (
        <ul className="td-picker__list" data-testid="todo-link-options">
          {opts.projects.map((p) => <li key={p.id}><button onClick={() => { onPick({ client_id: p.client_id, project_id: p.id }); setOpts(null); setQ(""); }} data-testid={`todo-link-opt-project-${p.id}`}>Project · {p.name}<span>{p.client_name}</span></button></li>)}
          {opts.clients.map((c) => <li key={c.id}><button onClick={() => { onPick({ client_id: c.id, project_id: null }); setOpts(null); setQ(""); }} data-testid={`todo-link-opt-client-${c.id}`}>Client · {c.full_name}</button></li>)}
        </ul>
      )}
    </div>
  );
};

const Lightbox = ({ path, version, onClose }) => {
  const url = useAuthImage(path, version);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="td-lightbox" data-testid="todo-lightbox">
        <DialogTitle className="sr-only">Image</DialogTitle>
        {url ? <img src={url} alt="Attachment" data-testid="todo-lightbox-img" /> : <p className="hx-muted">Loading…</p>}
      </DialogContent>
    </Dialog>
  );
};

const Attachments = ({ t, onTodo }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const [edit, setEdit] = useState(null);
  const base = (a) => `/todos/${t.id}/attachments/${a.id}`;
  const upload = async (files) => {
    setBusy(true);
    for (const f of files) {
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name} is over 15 MB`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try { const { data } = await adminApi.post(`/todos/${t.id}/attachments`, fd); onTodo(data); } catch (e) { err(e); }
    }
    setBusy(false);
    input.current.value = "";
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.original_name}?`)) return;
    try { const { data } = await adminApi.delete(base(a)); onTodo(data); } catch (e) { err(e); }
  };
  return (
    <div>
      <div className="td-atts" data-testid="todo-attachments">
        {t.attachments.map((a) => (
          <figure key={a.id} className="td-att" data-testid={`todo-att-${a.id}`}>
            <AuthImage path={`${base(a)}?thumb=1`} version={a.edited_at || a.created_at} alt={a.original_name} className="td-att__img" onClick={() => setView(a)} testId={`todo-att-thumb-${a.id}`} />
            <figcaption>
              <span className="td-att__name">{a.original_name}</span>
              {a.edited_at && <span className="td-att__edited" data-testid={`todo-att-edited-${a.id}`}>Edited {fmtDate(a.edited_at)}</span>}
              <span className="td-att__tools">
                <button className="hx-btn hx-btn--ghost hx-btn--sm" onClick={() => setEdit(a)} data-testid={`todo-att-markup-${a.id}`}><Brush size={12} /> Mark up</button>
                <button className="td-icon" onClick={() => remove(a)} aria-label="Delete attachment" data-testid={`todo-att-delete-${a.id}`}><Trash2 size={13} /></button>
              </span>
            </figcaption>
          </figure>
        ))}
        <button className="td-att td-att--add" disabled={busy} onClick={() => input.current.click()} data-testid="todo-att-upload"><Upload size={16} />{busy ? "Uploading…" : "Add image"}<span>JPEG or PNG · 15 MB</span></button>
      </div>
      <input ref={input} type="file" accept="image/jpeg,image/png" multiple hidden onChange={(e) => upload([...e.target.files])} data-testid="todo-att-input" />
      {view && <Lightbox path={base(view)} version={view.edited_at || view.created_at} onClose={() => setView(null)} />}
      {edit && <ImageMarkup todoId={t.id} att={edit} onClose={() => setEdit(null)} onSaved={(d) => { onTodo(d); setEdit(null); }} />}
    </div>
  );
};

export const TodoDetail = ({ id, people, onClose, onChanged, onMoved }) => {
  const [t, setT] = useState(null);
  const [form, setForm] = useState(null);
  const [moving, setMoving] = useState("");
  const [newName, setNewName] = useState("");
  const load = useCallback(() => adminApi.get(`/todos/${id}`).then((r) => { setT(r.data); setForm({ title: r.data.title, description: r.data.description, due_date: r.data.due_date || "" }); }), [id]);
  useEffect(() => { load(); }, [load]);
  const apply = (data) => { setT(data); onChanged(); };
  const patch = (body) => adminApi.patch(`/todos/${id}`, body).then((r) => apply(r.data)).catch(err);
  const dirty = t && form && (form.title !== t.title || form.description !== t.description || (form.due_date || null) !== (t.due_date || null));
  const save = () => patch({ title: form.title, description: form.description, ...(form.due_date ? { due_date: form.due_date } : { clear_due_date: true }) }).then(() => toast.success("Saved"));
  const move = async () => {
    const body = moving === "__new" ? { new_person_name: newName } : { person_id: moving };
    try { const { data } = await adminApi.post(`/todos/${id}/move`, body); setT(data); setMoving(""); setNewName(""); toast.success(`Moved to ${data.moved_to.name}`); onMoved(data.moved_to); } catch (e) { err(e); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this to-do? This can't be undone.")) return;
    try { await adminApi.delete(`/todos/${id}`); toast.success("To-do deleted"); onChanged(); onClose(); } catch (e) { err(e); }
  };
  const owner = t && people.find((p) => p.id === t.person_id);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="td-detail" data-testid="todo-detail">
        <DialogTitle className="sr-only">To-do details</DialogTitle>
        {!t ? <p className="hx-muted">Loading…</p> : (
          <div className="td-detail__body">
            <input className="td-detail__title" value={form.title} maxLength={200} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="todo-detail-title" />
            <p className="hx-muted">Created {fmtTime(t.created_at)}{owner ? ` · ${owner.name}'s list` : ""}</p>
            <div className="td-detail__grid">
              <label className="hx-field"><span className="hx-label">Status</span><StatusSelect value={t.status} onChange={(s) => patch({ status: s })} testId="todo-detail-status" /></label>
              <label className="hx-field"><span className="hx-label">Due date (optional)</span>
                <span style={{ display: "flex", gap: 6 }}><input className="hx-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="todo-detail-due" />
                  {form.due_date && <button className="td-icon" onClick={() => setForm({ ...form, due_date: "" })} aria-label="Clear due date" data-testid="todo-detail-due-clear"><X size={14} /></button>}</span></label>
            </div>
            <label className="hx-field"><span className="hx-label">Description</span>
              <textarea className="hx-input" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="todo-detail-description" /></label>
            {dirty && <div><button className="hx-btn hx-btn--sm" disabled={!form.title.trim()} onClick={save} data-testid="todo-detail-save">Save changes</button></div>}
            <div className="hx-field"><span className="hx-label">Studio link (optional)</span>
              <LinkPicker client={t.client} project={t.project} onPick={(l) => patch(l)} onClear={() => patch({ clear_link: true })} /></div>
            <div className="hx-field"><span className="hx-label">Images</span><Attachments t={t} onTodo={apply} /></div>
            <div className="hx-field"><span className="hx-label">Move to…</span>
              <span className="td-move">
                <select className="hx-input" value={moving} onChange={(e) => setMoving(e.target.value)} data-testid="todo-move-select">
                  <option value="">Choose a person</option>
                  {people.filter((p) => p.id !== t.person_id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  <option value="__new">+ New person…</option>
                </select>
                {moving === "__new" && <input className="hx-input" placeholder="New person's name" value={newName} maxLength={60} onChange={(e) => setNewName(e.target.value)} data-testid="todo-move-new-name" />}
                <button className="hx-btn hx-btn--ghost hx-btn--sm" disabled={!moving || (moving === "__new" && !newName.trim())} onClick={move} data-testid="todo-move-confirm">Move</button>
              </span></div>
            <div className="hx-field"><span className="hx-label">History</span>
              <ul className="td-history" data-testid="todo-history">{t.history.slice().reverse().map((h) => <li key={`${h.at}-${h.type}-${h.to}`}><span>{HIST[h.type](h)}</span><time>{fmtTime(h.at)}</time></li>)}</ul></div>
            <div className="td-detail__foot">
              <button className="hx-btn hx-btn--ghost hx-btn--sm hx-btn--danger" onClick={remove} data-testid="todo-delete"><Trash2 size={13} /> Delete to-do</button>
              <button className="hx-btn hx-btn--sm" onClick={onClose} data-testid="todo-detail-close">Done</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
